import { useEffect } from 'react';
import { InfiniteData, useInfiniteQuery, UseInfiniteQueryOptions } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import { queryClient, QUERY_KEYS } from '@src/query';
import { ApiError } from '@src/services/api';
import { ApiTypes, MediaFacet, MediaQuery } from 'common';
import { searchCollectionMedias } from './api';

const GET_MEDIA_LIMIT = 25;

/**
 * Get collection medias for the given collectionId and query params
 * @param collectionId
 * @param params
 * @param config
 */
export const useQueryCollectionMedias = (
  collectionId: string,
  params: {
    token?: string
    query: Omit<MediaQuery, 'cursor'>
  },
  config?: Omit<UseInfiniteQueryOptions<ApiTypes.ResGetCollectionMedias, ApiError, InfiniteData<ApiTypes.ResGetCollectionMedias>>, 'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'>,
) => {
  const query = useInfiniteQuery({
    queryKey: QUERY_KEYS.collectionMedia(collectionId, params),
    queryFn: ({ pageParam: cursor }) => {
      return searchCollectionMedias(collectionId, {
        ...params.query,
        cursor: cursor as string | undefined,
        limit: typeof params.query.limit === 'number' ? params.query.limit : GET_MEDIA_LIMIT,
      }, params.token);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastRes) => lastRes.cursor,
    gcTime: 0,
    ...config,
  });

  useEffect(() => {
    if (query.data) {
      query.data.pages.forEach((res) =>
        res.medias.forEach((media) => queryClient.setQueryData(QUERY_KEYS.media(media._id), media))
      );
    }
  }, [query.data]);

  return query;
};

/**
 * Fetch facets for medias inside a collection without loading media results.
 * This is the collection-scoped equivalent of useMediaFindFacet.
 */
export const useQueryCollectionMediaFacet = (
  collectionId: string,
  params: {
    token?: string
    query: Omit<MediaQuery, 'cursor' | 'limit' | 'facets'>
    input: string
    facets: MediaFacet[]
    debounce?: number
  },
  config?: Omit<UseInfiniteQueryOptions<ApiTypes.ResGetCollectionMedias, ApiError, InfiniteData<ApiTypes.ResGetCollectionMedias>>, 'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'>,
) => {
  const [debouncedInput] = useDebounce(params.input, params.debounce ?? 0);

  return useQueryCollectionMedias(
    collectionId,
    {
      token: params.token,
      query: {
        ...params.query,
        limit: 0,
        facets: {
          fields: params.facets,
          input: debouncedInput,
        },
      },
    },
    config,
  );
};
